import { useCallback, useEffect, useState } from "react";
import api, { API_BASE, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { IdCard, RotateCw, ExternalLink, PlugZap, Settings } from "lucide-react";
import { toast } from "sonner";
import HrTabs from "@/pages/hr/HrTabs";

// HR module — the Quickair HR app itself (Hostinger), served on the ERP's own
// origin by backend/hr_bridge.py at /api/hr/app/. Each person signs in with
// their own HR account: HR admins get HR's admin panel, employees their own
// employee panel. HR enforces every permission server-side.

const errText = (e) => formatApiError(e?.response?.data?.detail) || e?.message || "Something went wrong";

function ConnectionDialog({ open, onOpenChange, onSaved }) {
  const [cfg, setCfg] = useState(null);
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open) return;
    setResult(null);
    api.get("/hr/settings").then(({ data }) => { setCfg(data); setUrl(data.url); }).catch((e) => toast.error(errText(e)));
  }, [open]);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/hr/settings", { url });
      setCfg(data);
      toast.success("HR app URL saved");
      onSaved();
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  const test = async () => {
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post("/hr/settings/test");
      setResult({ ok: true, text: `The HR app answered${data.version ? ` (version ${data.version})` : ""}.` });
    } catch (e) { setResult({ ok: false, text: errText(e) }); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="hr-connection">
        <DialogHeader><DialogTitle>Connection to the HR app</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-slate-600">
            The address of the Quickair HR app on Hostinger. The ERP shows that app here; people sign in with their own
            HR accounts, so no HR password is stored in the ERP.
          </p>
          {cfg?.from_env?.length > 0 && <p className="text-xs text-amber-700">Set on the server (overrides this form): HR_APP_URL</p>}
          <div className="space-y-1.5">
            <Label htmlFor="hr-url">HR app URL</Label>
            <Input id="hr-url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://your-domain/HR/" data-testid="hr-cfg-url" />
          </div>
          {result && (
            <div className={`rounded-md border px-3 py-2 text-sm ${result.ok ? "border-green-200 bg-green-50 text-green-800" : "border-red-200 bg-red-50 text-red-700"}`} data-testid="hr-cfg-result">
              {result.text}
            </div>
          )}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={test} disabled={busy || !cfg?.url} data-testid="hr-cfg-test">
            <PlugZap className="w-4 h-4 mr-1" /> Test connection
          </Button>
          <Button onClick={save} disabled={busy} data-testid="hr-cfg-save">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function HrModule() {
  const [status, setStatus] = useState(null);
  const [error, setError] = useState("");
  const [frameKey, setFrameKey] = useState(0);
  const [showConn, setShowConn] = useState(false);
  const appUrl = `${API_BASE}/hr/app/`;

  const loadStatus = useCallback(async () => {
    try {
      const { data } = await api.get("/hr/status");
      setStatus(data);
      setError("");
    } catch (e) { setError(errText(e)); }
  }, []);
  useEffect(() => { loadStatus(); }, [loadStatus]);

  return (
    <div className="flex flex-col gap-3 -m-2 md:-m-4 max-md:m-0" data-testid="hr-module">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2" style={{ fontFamily: "Manrope" }}>
          <IdCard className="w-5 h-5 text-blue-600" /> HR
        </h1>
        <div className="flex items-center gap-2">
          {status?.configured && <>
            <Button size="sm" variant="outline" onClick={() => setFrameKey((k) => k + 1)} data-testid="hr-reload">
              <RotateCw className="w-3.5 h-3.5 mr-1.5" /> Reload
            </Button>
            <Button size="sm" variant="outline" asChild>
              <a href={appUrl} target="_blank" rel="noopener noreferrer" data-testid="hr-open-full">
                <ExternalLink className="w-3.5 h-3.5 mr-1.5" /> Full screen
              </a>
            </Button>
          </>}
          {status?.is_admin && (
            <Button size="sm" variant="outline" onClick={() => setShowConn(true)} data-testid="hr-open-connection">
              <Settings className="w-3.5 h-3.5 mr-1.5" /> Connection
            </Button>
          )}
        </div>
      </div>
      <HrTabs />

      {error && <div className="rounded-md border border-red-200 bg-red-50 text-red-700 text-sm px-3 py-2">{error}</div>}
      {status && !status.configured && (
        <div className="rounded-md border border-amber-200 bg-amber-50 text-amber-800 text-sm px-3 py-2" data-testid="hr-not-configured">
          The HR module isn't connected yet{status.is_admin ? " — set the HR app URL under Connection." : " — ask an admin to connect it."}
        </div>
      )}

      {status?.configured && (
        <iframe
          key={frameKey}
          src={appUrl}
          title="Quickair HR"
          className="w-full h-[calc(100vh-8.5rem)] min-h-[520px] max-md:h-[calc(100dvh-15.5rem)] max-md:min-h-[420px] rounded-lg border border-slate-200 bg-white"
          // No allow-top-navigation: the HR app can never navigate the ERP away.
          sandbox="allow-scripts allow-same-origin allow-forms allow-downloads allow-modals allow-popups allow-popups-to-escape-sandbox"
          data-testid="hr-frame"
        />
      )}

      {status?.is_admin && (
        <ConnectionDialog open={showConn} onOpenChange={setShowConn} onSaved={() => { loadStatus(); setFrameKey((k) => k + 1); }} />
      )}
    </div>
  );
}
